import React from "react";
import {
  Box,
  Typography,
  Paper,
  Divider,
  IconButton
} from "@mui/material";
import LoggedInLayout from "../../layout/LoggedInLayout";
import { ArrowBack } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

const styles = {
  header: {
    display: "flex",
    alignItems: "center",
    backgroundColor: "rgba(243, 243, 245, 1)",
    height: "46px",
    borderRadius: "6px",
    paddingLeft: "12px",
    marginLeft: "32px",
    marginBottom: "24px",
    width: "95%",
  },

  backBtn: {
    padding: 0,
    marginRight: "8px",
    width: "28px",
    height: "28px",
    backgroundColor: "rgba(255, 255, 255, 1)",
    borderRadius: "50%",
    boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
  },

  backIcon: {
    fontSize: "22px",
    color: "rgba(157, 158, 158, 1)",
  },

  pageHeading: {
    fontSize: "15px",
    fontWeight: 500,
    color: "#231F20",
  },

  outerCard: {
    borderRadius: 3,
    padding: "18px 10px",
    display: "flex",
    flexDirection: "column",
    gap: 1,
    justifyContent: "space-between",
    position: "relative",
    height: "auto",
    width:"auto",
    maxWidth:"700px"
    

  },

  stepLabel: {
    textAlign: "center",
    fontSize: "15px",
    fontWeight: 600,
    mb: 1,
  },

  innerCard: {
    backgroundColor: "white",
    p: 2,
    width: "100%",
    marginTop: "50px",
  },

  cardTitle: {
    fontSize: "15px",
    fontWeight: 600,
    textAlign: "center",
  },

  cardTitleBox: {
    position: "absolute",
    top: 70,
    left: 0,
    right: 0,
    backgroundColor: "white",
    padding: "10px 15px",
    width: "190px",
    boxShadow: "0px 4px 12px rgba(250, 240, 240, 0.88)",
    margin: "0 auto",
    borderRadius: "8px",
    zIndex: 8,
    textAlign: "center",
    height: "65px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  description: {
    fontSize: "15px",
    fontWeight: 400,
    color: "#231F20",
    textAlign: "left",
    mt: 2,
  },

  canText: {
    fontSize: "15px",
    fontWeight: 600,
    mt: 1,
  },

  list: {
    paddingLeft: "20px",
    margin: 0,
  },

  listItem: {
    fontSize: "14px",
    color: "#374151",
    marginBottom: "4px",
  },

  bottomText: {
    marginTop: "12px",
    padding: "12px",
  },

  bottomLine: {
    fontSize: "14px",
    color: "#0F172A",
    fontWeight: 500,
  },
};

const steps = [
  {
    step: "First Step",
    title: "Discover Yourself",
    descriptions: [
      `Create a complete academic and personal profile by sharing marks, parental inputs, and completing scientifically designed aptitude and personality assessments. This step uncovers how you think, learn, and grow—forming the foundation for precise, unbiased career guidance.`,
    ],
    bottom: [
      "Output: A complete student profile built on data, science, and self-reflection",
    ],
    bg: "#9FE3E7",
  },
  {
    step: "Second Step",
    title: "Get Your Career Report",
    descriptions: [
      `Get a personalized career report based on data, assessments, and proven methodology.`,
      `It highlights best-fit career clusters, realistic and aspirational pathways, strengths, gaps, and clear reasoning—so you understand not just what suits you, but why.`,
    ],
    bottom: [
      "Output: A structured career report that shows what suits you and why",
    ],
    bg: "#FFE6B3",
  },
  {
    step: "Third Step",
    title: "Build Your Career Roadmap",
    descriptions: [
      `Translate career insights into a structured development plan. Define skills to build, academic focus areas, milestones, and timelines—with guidance from our AI Career Buddy and experienced counsellors—aligned to your goals and individual context.`,
    ],
    bottom: [
      "Output: A step-by-step roadmap designed for your goal, pace, and situation.",
    ],
    bg: "#FADADD",
  },
  {
    step: "Final Step",
    title: "Act, Track & Grow",
    descriptions: [
      "Act on your roadmap with curated learning resources and skill partners.",
      "Track progress, review outcomes, and refine your plan over time—ensuring your career decisions stay aligned with your strengths, aspirations, and changing opportunities.",
    ],
    bottom: [
      "Output: Continuous growth, informed decisions, and long-term career confidence.",
    ],
    bg: "#D9F5E5",
  },
];

const KnowTheProcess = () => {
  const navigate = useNavigate();

  return (
    <LoggedInLayout>
      <Box className="page-container">

        {/* Header */}
        <Box sx={styles.header}>
          <IconButton onClick={() => navigate(-1)} sx={styles.backBtn}>
            <ArrowBack sx={styles.backIcon} />
          </IconButton>

          <Typography sx={styles.pageHeading}>
            Know The Process
          </Typography>
        </Box>

        {/* Description */}
        <Box sx={{ ml: 8, mb: 4, width: "90%" }}>
          <Typography sx={{ fontSize: "16px", fontWeight: 400 }}>
            At SwayamRise, we believe clarity comes from process. Our end-to-end
            career guidance framework helps you understand yourself, explore
            the right career options, create a roadmap to develop the right
            skills and make well-informed choices through a structured,
            step-by-step journey with clarity and direction.
          </Typography>
        </Box>

        {/* Flex Cards */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 3,
            px: 4,
          }}
        >
          {steps.map((item, index) => (
            <Box
              key={index}
              sx={{
                flex: "1 1 260px",
                maxWidth: "280px",
                minWidth: "280px",
                width:"auto"
              }}
            >
              <Box sx={{ ...styles.outerCard, backgroundColor: item.bg }}>

                <Typography sx={styles.stepLabel}>
                  {item.step}
                </Typography>

                <Paper elevation={0} sx={styles.innerCard}>
                  <Box sx={styles.cardTitleBox}>
                    <Typography sx={styles.cardTitle}>
                      {item.title}
                    </Typography>
                  </Box>

                  <Box sx={{ mt: 3 }}>
                    {item.descriptions.map((text, i) => (
                      <Typography key={i} sx={styles.description}>
                        {text}
                      </Typography>
                    ))}
                  </Box>

                  {item.can?.length > 0 && (
                    <Box>
                      <Divider sx={{ my: 1.5 }} />

                      <Typography sx={styles.canText}>
                        You can:
                      </Typography>

                      <Box component="ul" sx={styles.list}>
                        {item.can.map((point, i) => (
                          <li key={i}>
                            <Typography sx={styles.listItem}>
                              {point}
                            </Typography>
                          </li>
                        ))}
                      </Box>
                    </Box>
                  )}
                </Paper>

                <Box sx={styles.bottomText}>
                  {item.bottom.map((line, i) => (
                    <Typography key={i} sx={styles.bottomLine}>
                      {line}
                    </Typography>
                  ))}
                </Box>

              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </LoggedInLayout>
  );
};

export default KnowTheProcess;